<template>
  <div class="shopify-storefront-manager">
    <div class="manager-header">
      <div>
        <div class="manager-title">{{ title }}</div>
        <div class="manager-subtitle">{{ subtitle }}</div>
      </div>
      <div class="manager-header-actions">
        <div class="shell-path">
          Embedded app shell:
          <code>/shopify/app</code>
        </div>
        <v-btn outline :loading="loading.list" @click="refreshStores">
          Refresh stores
        </v-btn>
      </div>
    </div>

    <div
      v-if="message.text"
      :class="['banner', message.type ? `banner--${message.type}` : '']"
    >
      {{ message.text }}
    </div>

    <div class="manager-grid">
      <section class="stores-panel">
        <div class="panel-head">
          <div>
            <div class="panel-title">Shopify stores</div>
            <div class="panel-subtitle">
              Save multiple Shopify store configs, then complete Shopify app
              auth per store before the embed goes live.
            </div>
          </div>
          <v-btn outline small @click="startNewStore">New store</v-btn>
        </div>

        <div v-if="!stores.length" class="empty-state">
          <div class="empty-title">No Shopify stores saved yet.</div>
          <div class="empty-copy">
            Create your first store profile, save the config, then start the
            Shopify install flow to authenticate it.
          </div>
        </div>

        <button
          v-for="store in stores"
          :key="store.shop"
          type="button"
          :class="['store-card', { selected: selectedShop === store.shop }]"
          @click="selectStore(store.shop)"
        >
          <div class="store-card-top">
            <div class="store-shop">{{ store.shop }}</div>
            <div
              :class="[
                'store-status',
                ((store.installation || {}).status || 'pending').toLowerCase()
              ]"
            >
              {{ storeStatusLabel(store) }}
            </div>
          </div>
          <div class="store-meta">
            <span>{{ widgetSummaryLabel(store, 'feedback') }}</span>
            <span>{{ widgetSummaryLabel(store, 'proof') }}</span>
          </div>
        </button>
      </section>

      <section class="editor-panel">
        <div class="panel-head">
          <div>
            <div class="panel-title">Store configuration</div>
            <div class="panel-subtitle">
              Choose the campaign, set storefront defaults, then connect the
              store through Shopify auth before enabling the embed in theme
              editor.
            </div>
          </div>
        </div>

        <div
          v-if="selectedShop && !selectedStoreIsInstalled"
          class="banner banner--warning"
        >
          This store only has a saved Loyagram config. Shopify app auth has not
          completed yet. Use <strong>Save & connect Shopify</strong> to install
          and authenticate the app for this store.
        </div>

        <div class="form-row">
          <div class="field">
            <label>Shop domain</label>
            <v-text-field
              v-model="form.shop"
              solo
              flat
              placeholder="your-store.myshopify.com"
              @blur="normalizeShop"
            ></v-text-field>
          </div>
          <div class="field">
            <label>Campaign</label>
            <v-select
              v-model="form.campaign_id"
              :items="campaignOptions"
              item-text="label"
              item-value="value"
              solo
              flat
              placeholder="Select a campaign"
            ></v-select>
          </div>
        </div>

        <div class="form-row compact">
          <div class="field field-small">
            <label>Metric</label>
            <v-select
              v-model="form.metric_type"
              :items="metricTypes"
              solo
              flat
            ></v-select>
          </div>
          <div class="field field-small">
            <label>Mount selector</label>
            <v-text-field
              v-model="form.target"
              solo
              flat
              :placeholder="targetPlaceholder"
            ></v-text-field>
          </div>
          <div class="field field-toggle">
            <label>Storefront status</label>
            <v-switch
              v-model="form.enabled"
              color="accent"
              :label="form.enabled ? 'Enabled' : 'Paused'"
              hide-details
              class="toggle-switch"
            ></v-switch>
          </div>
        </div>

        <div class="form-row">
          <div class="field">
            <label>{{ scriptLabel }}</label>
            <v-text-field v-model="form.widget_src" solo flat></v-text-field>
          </div>
          <div class="field">
            <label>Widget config endpoint</label>
            <v-text-field
              v-model="form.config_endpoint"
              solo
              flat
            ></v-text-field>
          </div>
        </div>

        <div class="form-row compact">
          <div class="field field-small">
            <label>Page targeting</label>
            <v-select
              v-model="form.page_settings.selection"
              :items="pageOptions"
              item-text="label"
              item-value="id"
              solo
              flat
            ></v-select>
          </div>
          <div class="field">
            <label>URL rules</label>
            <textarea
              v-model="pageSettingsText"
              class="urls-textarea"
              :disabled="
                form.page_settings.selection === 'ALL_PAGES' ||
                  form.page_settings.selection === 'NOWHERE'
              "
              placeholder="/products/*
/collections/*
/products/*?variant=*"
            ></textarea>
          </div>
        </div>

        <div class="actions">
          <v-btn
            color="accent"
            depressed
            :loading="loading.save"
            @click="saveConfig"
          >
            Save config
          </v-btn>
          <v-btn
            color="primaryLight1"
            dark
            depressed
            :loading="loading.connect"
            :disabled="!canSave"
            @click="saveAndLaunch"
          >
            {{ connectButtonLabel }}
          </v-btn>
          <v-btn
            outline
            :loading="loading.validate"
            :disabled="!normalizedShop"
            @click="validateConfig"
          >
            Validate config
          </v-btn>
          <v-btn
            outline
            :disabled="!validation || !validation.preview_url"
            @click="openPreview"
          >
            Open preview
          </v-btn>
          <v-btn
            outline
            color="error"
            :disabled="!selectedShop"
            :loading="loading.delete"
            @click="disconnectStore"
          >
            {{ deleteButtonLabel }}
          </v-btn>
        </div>

        <div class="output-grid">
          <div class="output-panel">
            <div class="output-title">Live validation</div>
            <div class="output-copy">
              Validate the stored config, then open the exact embed preview that
              Shopify will use.
            </div>
            <textarea readonly :value="validationSummary"></textarea>
          </div>
          <div class="output-panel">
            <div class="output-title">Embed snippet</div>
            <div class="output-copy">
              Use this for debugging or manual installs. The theme extension
              will inject the same attributes automatically.
            </div>
            <textarea readonly :value="embedSnippet"></textarea>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import shopifyApi from '@/api/shopify'

function trimTrailingSlash(value) {
  return String(value || '').replace(/\/+$/, '')
}

function joinUrl(base, path) {
  const normalizedBase = trimTrailingSlash(base || '')
  const normalizedPath = path.charAt(0) === '/' ? path : `/${path}`

  if (!normalizedBase) {
    return normalizedPath
  }

  return `${normalizedBase}${normalizedPath}`
}

function clonePageSettings(settings) {
  const pageSettings = settings || {}
  return {
    selection: pageSettings.selection || 'ALL_PAGES',
    urls: Array.isArray(pageSettings.urls) ? pageSettings.urls.slice() : []
  }
}

export default {
  name: 'StorefrontManager',
  props: {
    widgetType: {
      type: String,
      default: 'feedback'
    },
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    targetPlaceholder: {
      type: String,
      default: '#loyagram-root'
    }
  },
  data() {
    const origin = trimTrailingSlash(window.location.origin)
    return {
      loading: {
        list: false,
        save: false,
        validate: false,
        delete: false,
        connect: false
      },
      message: {
        type: '',
        text: ''
      },
      stores: [],
      selectedShop: '',
      validation: null,
      metricTypes: ['NPS', 'CSAT', 'CES', 'ES'],
      pageOptions: [
        {
          id: 'ALL_PAGES',
          label: 'All pages'
        },
        {
          id: 'ALL_PAGES_EXCEPT',
          label: 'All pages except'
        },
        {
          id: 'SPECIFIC_PAGES',
          label: 'Only these pages'
        },
        {
          id: 'NOWHERE',
          label: 'Disabled everywhere'
        }
      ],
      form: this.buildDefaultForm(origin)
    }
  },
  computed: {
    widgetLabel() {
      return this.widgetType === 'proof' ? 'proof' : 'feedback'
    },
    scriptLabel() {
      return this.widgetType === 'proof'
        ? 'Proof script URL'
        : 'Widget script URL'
    },
    campaignOptions() {
      const campaigns =
        this.$store.getters['user/currentProjectCampaigns'] || []
      return campaigns.map(campaign => ({
        label: `${campaign.brand_name || 'Campaign'} (${campaign.id})`,
        value: campaign.id
      }))
    },
    normalizedShop() {
      const value = (this.form.shop || '').trim().toLowerCase()
      if (!value) {
        return ''
      }
      return value.replace(/^https?:\/\//, '').replace(/\/.*$/, '')
    },
    canSave() {
      return !!(this.normalizedShop && this.form.campaign_id)
    },
    backendBase() {
      return trimTrailingSlash(process.env.VUE_APP_API_BASE || '')
    },
    selectedStore() {
      return this.stores.find(item => item.shop === this.selectedShop) || null
    },
    selectedStoreStatus() {
      return (
        ((this.selectedStore && this.selectedStore.installation) || {})
          .status || 'pending'
      ).toLowerCase()
    },
    selectedStoreIsInstalled() {
      return this.selectedStoreStatus === 'installed'
    },
    connectButtonLabel() {
      return this.selectedStoreIsInstalled
        ? 'Open Shopify app'
        : 'Save & connect Shopify'
    },
    deleteButtonLabel() {
      return this.selectedStoreIsInstalled ? 'Disconnect' : 'Remove draft'
    },
    pageSettingsText: {
      get() {
        return (this.form.page_settings.urls || []).join('\n')
      },
      set(value) {
        this.form.page_settings.urls = String(value || '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean)
      }
    },
    launchUrl() {
      if (!this.canSave) {
        return '#'
      }

      const params = [
        `shop=${encodeURIComponent(this.normalizedShop)}`,
        `widget_type=${encodeURIComponent(this.widgetType)}`,
        `campaign_id=${encodeURIComponent(this.form.campaign_id)}`,
        `metric_type=${encodeURIComponent(this.form.metric_type)}`,
        `return_to=${encodeURIComponent(this.$route.fullPath)}`
      ]
      return `${joinUrl(this.backendBase, '/shopify/app/launch')}?${params.join(
        '&'
      )}`
    },
    embedSnippet() {
      if (this.validation && this.validation.embed_html) {
        return this.validation.embed_html
      }

      const lines = [
        '<script',
        `  src="${this.form.widget_src}"`,
        `  data-shop="${this.normalizedShop || 'your-store.myshopify.com'}"`,
        `  data-api-base="${this.form.api_base}"`,
        `  data-config-endpoint="${this.form.config_endpoint}"`,
        `  data-widget-type="${this.widgetType}"`,
        `  data-enabled="${String(!!this.form.enabled)}"`,
        this.form.target ? `  data-target="${this.form.target}"` : '',
        this.form.metric_type
          ? `  data-metric-type="${this.form.metric_type}"`
          : '',
        '  defer',
        '><\\/script>'
      ]
      return lines.filter(Boolean).join('\n')
    },
    validationSummary() {
      if (!this.validation) {
        return 'Run validation to fetch the exact live config URL, theme editor link, and preview page.'
      }

      return [
        `Config URL: ${this.validation.public_config_url || 'n/a'}`,
        `Theme editor: ${this.validation.theme_editor_url || 'n/a'}`,
        `Preview: ${this.validation.preview_url || 'n/a'}`
      ].join('\n')
    }
  },
  mounted() {
    this.refreshStores()
  },
  methods: {
    buildDefaultForm(origin) {
      const campaign = this.$store.getters['user/currentCampaign'] || {}
      return {
        shop: '',
        campaign_id: campaign.id || '',
        metric_type: 'NPS',
        enabled: true,
        api_base: origin,
        widget_src:
          origin +
          (this.widgetType === 'proof'
            ? '/proof/proof.loyagram.js'
            : '/widget/loyagram.js'),
        config_endpoint: origin + '/shopify/storefront/widget-config',
        target: '',
        page_settings: {
          selection: 'ALL_PAGES',
          urls: []
        }
      }
    },
    storeStatusLabel(store) {
      const status = (
        (store.installation || {}).status || 'pending'
      ).toLowerCase()
      if (status === 'installed') {
        return 'Installed'
      }
      if (status === 'uninstalled') {
        return 'Uninstalled'
      }
      return 'Pending auth'
    },
    widgetSummaryLabel(store, widgetType) {
      const widget = (store.widgets || {})[widgetType] || {}
      if (!widget.campaign_id) {
        return `${widgetType}: not configured`
      }
      return `${widgetType}: ${widget.enabled === false ? 'paused' : 'ready'}`
    },
    setMessage(type, text) {
      this.message = { type, text }
    },
    normalizeShop() {
      this.form.shop = this.normalizedShop
    },
    startNewStore() {
      this.selectedShop = ''
      this.validation = null
      this.form = this.buildDefaultForm(
        trimTrailingSlash(window.location.origin)
      )
      this.setMessage('', '')
    },
    populateFromConfig(config) {
      this.form = Object.assign(
        this.buildDefaultForm(trimTrailingSlash(window.location.origin)),
        {
          shop: config.shop || this.form.shop,
          campaign_id: config.campaign_id || this.form.campaign_id,
          metric_type: config.metric_type || this.form.metric_type,
          enabled: config.enabled !== false,
          api_base: config.api_base || this.form.api_base,
          widget_src: config.widget_src || this.form.widget_src,
          config_endpoint: config.config_endpoint || this.form.config_endpoint,
          target: config.target || '',
          page_settings: clonePageSettings(config.page_settings)
        }
      )
    },
    applyStoreSummary(store) {
      const widget = (store.widgets || {})[this.widgetType] || {}
      this.selectedShop = store.shop
      this.validation = null
      this.populateFromConfig(
        Object.assign({}, widget, {
          shop: store.shop
        })
      )
    },
    refreshStores(selectedShop) {
      this.loading.list = true
      return shopifyApi
        .listStores()
        .then(res => {
          this.stores = res.data.results || []

          if (!this.stores.length) {
            this.startNewStore()
            return
          }

          const targetShop =
            selectedShop ||
            this.selectedShop ||
            (this.stores[0] && this.stores[0].shop)

          if (targetShop) {
            const store = this.stores.find(item => item.shop === targetShop)
            if (store) {
              this.applyStoreSummary(store)
            }
          }
        })
        .finally(() => {
          this.loading.list = false
        })
    },
    selectStore(shop) {
      const store = this.stores.find(item => item.shop === shop)
      if (store) {
        this.applyStoreSummary(store)
      }
    },
    saveConfig() {
      if (!this.canSave) {
        this.setMessage('error', 'Pick a shop and campaign first.')
        return Promise.resolve(null)
      }

      this.loading.save = true
      this.setMessage('', '')
      this.validation = null
      return shopifyApi
        .saveStorefrontConfig({
          shop: this.normalizedShop,
          widget_type: this.widgetType,
          campaign_id: this.form.campaign_id,
          metric_type: this.form.metric_type,
          enabled: this.form.enabled,
          api_base: this.form.api_base,
          widget_src: this.form.widget_src,
          config_endpoint: this.form.config_endpoint,
          target: this.form.target,
          page_settings: clonePageSettings(this.form.page_settings)
        })
        .then(res => {
          this.populateFromConfig(res.data.results || {})
          this.selectedShop = this.normalizedShop
          this.setMessage('success', 'Store configuration saved.')
          return this.refreshStores(this.normalizedShop).then(
            () => res.data.results
          )
        })
        .catch(() => {
          this.setMessage(
            'error',
            'Failed to save the Shopify store configuration.'
          )
          return null
        })
        .finally(() => {
          this.loading.save = false
        })
    },
    saveAndLaunch() {
      this.loading.connect = true
      return this.saveConfig()
        .then(result => {
          if (!result) {
            return
          }
          window.open(this.launchUrl, '_blank')
        })
        .finally(() => {
          this.loading.connect = false
        })
    },
    validateConfig() {
      if (!this.normalizedShop) {
        this.setMessage('error', 'Enter a valid .myshopify.com domain first.')
        return Promise.resolve(null)
      }

      this.loading.validate = true
      this.setMessage('', '')
      return shopifyApi
        .validateStore(this.normalizedShop, this.widgetType)
        .then(res => {
          this.validation = res.data.results || null
          this.setMessage('success', 'Live widget config validated.')
          return this.validation
        })
        .catch(() => {
          this.validation = null
          this.setMessage(
            'error',
            'Store config is not ready yet. Save it first, then complete Shopify auth.'
          )
          return null
        })
        .finally(() => {
          this.loading.validate = false
        })
    },
    openPreview() {
      if (this.validation && this.validation.preview_url) {
        window.open(this.validation.preview_url, '_blank')
      }
    },
    disconnectStore() {
      const shop = this.selectedShop || this.normalizedShop
      if (!shop) {
        return
      }

      const confirmationText = this.selectedStoreIsInstalled
        ? `Disconnect ${shop} from Loyagram and remove the saved Shopify store config?`
        : `Remove the saved draft config for ${shop}?`

      if (!window.confirm(confirmationText)) {
        return
      }

      this.loading.delete = true
      return shopifyApi
        .deleteStore(shop)
        .then(() => {
          this.setMessage(
            'success',
            this.selectedStoreIsInstalled
              ? 'Store disconnected.'
              : 'Draft store config removed.'
          )
          this.startNewStore()
          return this.refreshStores()
        })
        .catch(() => {
          this.setMessage(
            'error',
            this.selectedStoreIsInstalled
              ? 'Failed to disconnect the store.'
              : 'Failed to remove the draft store config.'
          )
        })
        .finally(() => {
          this.loading.delete = false
        })
    }
  }
}
</script>

<style lang="stylus">
.shopify-storefront-manager
  .manager-header
    display flex
    justify-content space-between
    align-items flex-start
    gap 20px
    margin-bottom 24px
  .manager-title
    font-size 22px
    font-weight 700
    color $color-primary-light-5
  .manager-subtitle
    margin-top 6px
    max-width 720px
    font-size 14px
    line-height 1.6
    color #6f7c8f
  .manager-header-actions
    display flex
    align-items center
    gap 12px
    flex-wrap wrap
    justify-content flex-end
  .shell-path
    padding 10px 14px
    border 1px solid #d8e0ea
    border-radius 999px
    background #fff
    font-size 13px
    color #6f7c8f
  .banner
    margin-bottom 20px
    padding 15px 18px
    border-radius 12px
    border 1px solid transparent
    font-size 14px
    font-weight 600
    line-height 1.6
    strong
      color inherit
      font-weight 800
    &.banner--success
      background #1f8f45
      border-color #16733a
      color #fff
    &.banner--error
      background #c92a2a
      border-color #a61e1e
      color #fff
    &.banner--warning
      background #fff3cd
      border-color #f0c36d
      color #5f3b00
  .manager-grid
    display grid
    grid-template-columns 320px minmax(0, 1fr)
    gap 22px
  .stores-panel,
  .editor-panel
    background #fff
    border 1px solid #e1e7ef
    border-radius 18px
    padding 22px
    box-shadow 0 14px 32px rgba(15, 23, 42, 0.04)
  .panel-head
    display flex
    justify-content space-between
    gap 16px
    align-items flex-start
    margin-bottom 18px
  .panel-title
    font-size 18px
    font-weight 700
    color $color-primary-light-5
  .panel-subtitle
    margin-top 4px
    font-size 13px
    line-height 1.5
    color #6f7c8f
  .empty-state
    padding 18px
    border 1px dashed #d7dfe8
    border-radius 16px
    background #fafcff
  .empty-title
    font-size 15px
    font-weight 700
    color $color-primary-light-5
  .empty-copy
    margin-top 8px
    font-size 13px
    line-height 1.6
    color #6f7c8f
  .store-card
    width 100%
    margin-top 12px
    padding 16px
    border 1px solid #e1e7ef
    border-radius 14px
    background #fff
    text-align left
    cursor pointer
    transition border-color .15s ease, box-shadow .15s ease, transform .15s ease
    &:hover
      border-color #a6b7cb
      transform translateY(-1px)
    &.selected
      border-color $color-accent
      box-shadow 0 8px 20px rgba(15, 157, 122, 0.12)
  .store-card-top
    display flex
    justify-content space-between
    align-items center
    gap 12px
  .store-shop
    font-weight 700
    color $color-primary-light-5
    word-break break-word
  .store-status
    border-radius 999px
    padding 6px 10px
    font-size 11px
    font-weight 700
    text-transform uppercase
    letter-spacing .04em
    &.installed
      background #ecfdf5
      color #047857
    &.uninstalled
      background #fff1f2
      color #be123c
    &.pending
      background #fff7ed
      color #c2410c
  .store-meta
    margin-top 10px
    display grid
    gap 6px
    font-size 12px
    color #6f7c8f
  .form-row
    display grid
    grid-template-columns repeat(2, minmax(0, 1fr))
    gap 18px
    margin-bottom 14px
    &.compact
      grid-template-columns minmax(0, 180px) minmax(0, 1fr) minmax(0, 180px)
  .field
    label
      display block
      margin-bottom 6px
      font-size 12px
      font-weight 700
      text-transform uppercase
      letter-spacing .06em
      color #6f7c8f
  .field-toggle
    display flex
    flex-direction column
    justify-content flex-start
  .toggle-switch
    margin-top 0
    padding-top 10px
  .urls-textarea
    width 100%
    min-height 110px
    padding 14px
    border 1px solid #dde5ee
    border-radius 14px
    background #fbfcfe
    font-size 13px
    line-height 1.5
    resize vertical
    &:disabled
      color #9aa8b8
  .actions
    display flex
    flex-wrap wrap
    gap 12px
    margin-top 18px
  .output-grid
    display grid
    grid-template-columns repeat(2, minmax(0, 1fr))
    gap 16px
    margin-top 22px
  .output-panel
    padding 18px
    border 1px solid #e1e7ef
    border-radius 16px
    background #fbfcfe
    textarea
      margin-top 12px
      width 100%
      min-height 150px
      padding 14px
      border 1px solid #dde5ee
      border-radius 14px
      background #fff
      font-size 12px
      line-height 1.6
      resize vertical
  .output-title
    font-size 15px
    font-weight 700
    color $color-primary-light-5
  .output-copy
    margin-top 6px
    font-size 13px
    line-height 1.5
    color #6f7c8f

@media (max-width: 1200px)
  .shopify-storefront-manager
    .manager-grid
      grid-template-columns 1fr

@media (max-width: 900px)
  .shopify-storefront-manager
    .manager-header
      display block
    .manager-header-actions
      margin-top 14px
      justify-content flex-start
    .form-row,
    .form-row.compact,
    .output-grid
      grid-template-columns 1fr
</style>
